import React, { useState } from 'react';
import { clsx } from 'clsx';
import { ChevronRightIcon, ChevronDownIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { useRepoMarginCallLines } from '@/hooks';
import { formatCurrencyCode } from '@/utils/trades/mtm-calculations';
import { displayCashPool } from '@/utils/trades/cash-pool-display';
import { formatMarkPrice } from '@/utils/trades/mark-price-format';
import { byLabel, fxPairLabel, timeTitle } from '../history/history-utils';
import { TradeIdLink } from '../TradeIdLink';
import { ReasonBadge } from '@/components/ui/ReasonBadge';
import type { TradeDetailSearch } from '../../trade-detail-search';
import type { RepoMarginCall, RepoMarginCallDirection } from '@/types';

// LR-479: the cash-pool posting ledger — one shared, presentational component reused by the
// Cash Pool history drawer (opened from the MTM bar) and the History → Cash Pool subtab
// (CashPoolPostings). It renders posted RepoMarginCall rows newest-first (the API order) with a running
// balance (each row's cashpool_after) and a per-trade drill-down. The host passes the
// already-fetched calls; only the drill-down fetches, lazily per expanded row (LR-565) — the same
// split as the Stock-L/B RunLedger + RunLineItems pair.
//
// Styling mirrors MtmRunHistory / RepoRunDetail: gray palette, text-sm, h-[39px] rows, the same
// chevron disclosure. The drill-down derives the contract / COB margin prices from the frozen
// money fields (price = value / qty), so no extra backend fields are needed.

// Date in the desk's `YYYY-MM-DD` convention (LR-667), UTC components (LR-679): a posting made at
// 23:30 UTC must file under the same day here, in the run log and in the trade card's Activity.
// Time is 24h UTC with the zone spelled out beside it; the viewer's own clock is on hover.
const dateFmt = {
  format: (d: Date): string =>
    `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`,
};
const timeFmt = new Intl.DateTimeFormat('en-US', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: 'UTC',
});

/** + in our favour (green), − against us (red), 0 neutral — matches the MtmAggregateBar exposure. */
function netClass(value: number): string {
  return value < 0 ? 'text-red-600' : value > 0 ? 'text-green-600' : 'text-gray-900';
}

function directionLabel(direction: RepoMarginCallDirection): string {
  return direction === 'due_to_cp' ? 'Due to CP' : direction === 'due_to_us' ? 'Due to us' : 'Settled';
}

const DirectionPill: React.FC<{ direction: RepoMarginCallDirection }> = ({ direction }) => (
  <span
    className={clsx(
      'inline-flex items-center rounded-full px-1.5 py-0.5 text-xs font-medium',
      direction === 'due_to_us'
        ? 'bg-green-50 text-green-700'
        : direction === 'due_to_cp'
          ? 'bg-red-50 text-red-700'
          : 'bg-gray-100 text-gray-600',
    )}
  >
    {directionLabel(direction)}
  </span>
);

/** Per-trade margin price derived from a frozen line: value / qty. "—" when qty is 0. */
function marginPx(value: string, quantity: number): string {
  if (!quantity) return '—';
  return formatMarkPrice(Number(value) / quantity);
}

// Shared grid template so the header and every summary row line up (chevron + 5 columns).
// Date's content is fixed-width so it gets a modest share; "By" (a full email) gets the largest min
// + fr so it shows in full without truncating. Money columns (Net Due / Pool after) flex evenly.
const ROW_GRID = 'grid grid-cols-[16px_minmax(96px,1.2fr)_minmax(110px,1.1fr)_minmax(80px,0.8fr)_minmax(110px,1.1fr)_minmax(150px,1.5fr)] items-center gap-2';

// LR-565: fetches its own rows — the frozen breakdown is a line per repo position in the book, so
// it is a sub-resource rather than part of the history payload. `enabled` comes from the row's
// expanded state, so nothing is requested for a collapsed row. Mirrors RunLineItems.
const TradeBreakdown: React.FC<{
  callId: number;
  currency: string;
  enabled: boolean;
  origin: TradeDetailSearch;
}> = ({ callId, currency, enabled, origin }) => {
  const { data: lines, isLoading, error } = useRepoMarginCallLines(callId, enabled);

  if (isLoading) {
    return <p className="py-2 pl-6 pr-4 text-sm text-gray-500">Loading contributing trades…</p>;
  }
  if (error) {
    return (
      <p className="py-2 pl-6 pr-4 text-sm text-red-600">Failed to load contributing trades.</p>
    );
  }
  if (!lines || lines.length === 0) {
    return <p className="py-2 pl-6 pr-4 text-sm text-gray-500">No contributing trades.</p>;
  }
  // LR-579: only a Method B call with something accrued carries the extra column, so Method A and
  // pre-LR-579 frozen rows keep the breakdown unchanged.
  const showAccrued = lines.some((l) => Math.abs(Number(l.accrued_component ?? '0')) >= 0.005);
  return (
    // Auto layout + whitespace-nowrap on the money columns so figures (Market Value, Exposure…)
    // are never clipped to an ellipsis; the wrapper's overflow-x-auto scrolls if it ever exceeds.
    <table className="w-full">
      <thead>
        <tr className="h-[37px] border-b border-gray-200">
          <th className="whitespace-nowrap pl-6 pr-3 py-1.5 text-left text-[11px] font-semibold uppercase tracking-wide text-lr-faint">Trade ID</th>
          <th className="px-3 py-1.5 text-left text-[11px] font-semibold uppercase tracking-wide text-lr-faint">Symbol</th>
          <th className="whitespace-nowrap px-3 py-1.5 text-right text-[11px] font-semibold uppercase tracking-wide text-lr-faint">Qty</th>
          <th title="Margin-adjusted contract price (trade cash ÷ qty)" className="whitespace-nowrap px-3 py-1.5 text-right text-[11px] font-semibold uppercase tracking-wide text-lr-faint">Contract px</th>
          <th title="Margin-adjusted COB price (market value ÷ qty); differs from the raw COB (T-1) on the MTM page when margin ≠ 100%" className="whitespace-nowrap px-3 py-1.5 text-right text-[11px] font-semibold uppercase tracking-wide text-lr-faint">COB px</th>
          <th className="whitespace-nowrap px-3 py-1.5 text-right text-[11px] font-semibold uppercase tracking-wide text-lr-faint">Market Value</th>
          {showAccrued && (
            <th title="Unpaid accrued repo interest folded into this line's exposure (Method B). Signed CONTRIBUTION, not the raw interest: a repo owes it, so it enters negative and the row foots as Market Value − Contract px × Qty + Accrued." className="whitespace-nowrap px-3 py-1.5 text-right text-[11px] font-semibold uppercase tracking-wide text-lr-faint">Accrued</th>
          )}
          <th className="whitespace-nowrap px-3 py-1.5 text-right text-[11px] font-semibold uppercase tracking-wide text-lr-faint">Exposure</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((l, i) => {
          const exp = Number(l.exposure);
          const lineCcy = l.currency ?? currency;
          const converted =
            lineCcy.toUpperCase() !== currency.toUpperCase()
              ? `Converted at COB ${fxPairLabel(lineCcy, currency, l.fx_rate)}`
              : undefined;
          return (
            // `lines` is a JSONB blob served verbatim (no serializer validates it on the way out),
            // so guard the id even though every writer has always set it — the backend's own
            // pool_shares reader guards the same key. Index fallback keeps such rows from colliding
            // on an undefined key.
            <tr key={l.position_id ?? `i${i}`} className="h-[37px] border-b border-gray-100 hover:bg-lr-row-active">
              <td className="whitespace-nowrap pl-6 pr-3 py-1.5 text-sm">
                <TradeIdLink id={l.position_id} origin={origin} />
              </td>
              <td className="max-w-[160px] truncate px-3 py-1.5 text-sm text-gray-900">{l.symbol_id}</td>
              <td className="whitespace-nowrap px-3 py-1.5 text-right text-sm text-gray-900">{l.quantity}</td>
              <td className="whitespace-nowrap px-3 py-1.5 text-right text-sm text-gray-900">
                {marginPx(l.trade_cash, l.quantity)}
              </td>
              <td className="whitespace-nowrap px-3 py-1.5 text-right text-sm text-gray-900">
                {marginPx(l.market_value, l.quantity)}
              </td>
              <td className="whitespace-nowrap px-3 py-1.5 text-right text-sm text-gray-900">
                {formatCurrencyCode(Number(l.market_value), lineCcy)}
              </td>
              {showAccrued && (
                <td className="whitespace-nowrap px-3 py-1.5 text-right text-sm text-gray-500">
                  {formatCurrencyCode(Number(l.accrued_component ?? '0'), currency)}
                </td>
              )}
              <td className={clsx('whitespace-nowrap px-3 py-1.5 text-right text-sm', netClass(exp))}>
                {formatCurrencyCode(exp, currency)}
                {converted && (
                  <ReasonBadge
                    label="FX"
                    reason={converted}
                    className="ml-1 rounded bg-slate-100 px-1 py-px text-[10px] font-semibold leading-3 text-slate-500"
                  />
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

interface LedgerRowProps {
  call: RepoMarginCall;
  expanded: boolean;
  onToggle: () => void;
  origin: TradeDetailSearch;
}

const LedgerRow: React.FC<LedgerRowProps> = ({ call, expanded, onToggle, origin }) => {
  const netDue = Number(call.net_due);
  // LR-493: the cash pool (running balance) stays in the SETTLEMENT currency — it accumulates per
  // book in one currency; the per-trade breakdown (TradeBreakdown) shows Market Value in the trade
  // currency and Exposure in the call's (`call.currency`).
  const settleCcy = call.settlement_currency || call.currency;
  // LR-493: the Net Due (the call's bottom line) is shown in the currency it was PAID in (the pay-in
  // major), converting the settlement figure at the payment rate. No pay-in ⇒ settlement, rate 1.
  const netCcy = call.payment_currency || settleCcy;
  const netRate = call.payment_fx_rate ? Number(call.payment_fx_rate) : 1;
  // A manual override (LR-479) posts a magnitude that differs from the computed net_due (same sign).
  // A missing/non-numeric posted_amount (pre-override rows) means "posted == computed": fall back to
  // net_due so we never render NaN or a phantom override arrow.
  const postedRaw = Number(call.posted_amount);
  const posted = Number.isFinite(postedRaw) ? postedRaw : netDue;
  const overridden = posted !== netDue;
  const when = new Date(call.called_at);
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className={clsx(
          ROW_GRID,
          // Constant border-l-[3px] + pl-[9px]; only the colour + bg toggle so expanding never
          // shifts the row (content stays at 12px, aligned with the pl-3 header). Same interaction
          // language as the rail selected-row (border-l-lr-accent + bg-lr-row-active).
          'h-[39px] w-full border-b border-b-gray-200 border-l-[3px] pl-[9px] pr-5 text-left hover:bg-lr-row-active focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
          expanded ? 'border-l-lr-accent bg-lr-row-active' : 'border-l-transparent'
        )}
      >
        {expanded ? (
          <ChevronDownIcon className="h-4 w-4 shrink-0 text-lr-accent" aria-hidden />
        ) : (
          <ChevronRightIcon className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />
        )}
        <span
          className="min-w-0 truncate whitespace-nowrap text-sm text-gray-600"
          title={timeTitle(when)}
        >
          {dateFmt.format(when)} {timeFmt.format(when)} UTC
        </span>
        {/* Net Due = the COMPUTED call; under an override show "computed → posted". Rendered SIGNED
            (formatCurrencyCode adds +/−, 0 carries none): a Due to CP figure must read −1,000,000
            USD, not a red +1,000,000 — colour alone is not a direction (demo feedback #62). The
            override posted_amount carries the same sign as net_due (LR-479 locked), so it signs
            correctly too. */}
        <span className={clsx('min-w-0 truncate text-right text-sm font-medium', netClass(netDue))}>
          {formatCurrencyCode(netDue * netRate, netCcy)}
          {overridden && (
            <span className="ml-1 text-xs font-normal text-amber-700">
              → {formatCurrencyCode(posted * netRate, netCcy)}
            </span>
          )}
        </span>
        <span className="min-w-0 truncate">
          <DirectionPill direction={call.direction} />
        </span>
        {/* LR-542 (feedback #63): the pool is displayed billing-signed (flipped vs the stored
            ledger sign) — a Due-to-us posting DECREASES the shown Pool after. Neutral colour by
            design (variant A): the pool is a running balance, not an in-our-favour reading. */}
        <span className="min-w-0 truncate text-right text-sm text-gray-900">
          {formatCurrencyCode(displayCashPool(Number(call.cashpool_after)), settleCcy)}
        </span>
        {/* Full email (no truncation), then the notes icon (if any). title keeps the full value on
            hover should an unusually long address still exceed the widened track. */}
        <span className="flex min-w-0 items-center justify-start gap-1 text-sm text-gray-500" title={call.marked_by ?? undefined}>
          <span className="whitespace-nowrap">{byLabel(call.marked_by, call.source)}</span>
          {call.notes && <DocumentTextIcon className="h-4 w-4 shrink-0 text-gray-400" aria-hidden title={call.notes} />}
        </span>
      </button>
      {expanded && (
        <div className="overflow-x-auto bg-lr-panel border-l-[3px] border-lr-spine">
          <TradeBreakdown
            callId={call.id}
            currency={call.currency}
            enabled={expanded}
            origin={origin}
          />
          {/* LR-579: the methodology is FROZEN on the row, so a later flip of the counterparty's
              election never restates this posting — name the one it was actually called under. */}
          {call.methodology && (
            <p className="py-1.5 pl-6 pr-4 text-[11px] text-gray-400">
              {call.methodology === 'with_accrued'
                ? `Method B (price plus accrued) — includes ${formatCurrencyCode(Number(call.accrued_component ?? '0'), settleCcy)} of unpaid accrued repo interest`
                : 'Method A — price move only; accrued repo interest excluded'}
            </p>
          )}
          {/* LR-493: on a cross-settlement book the breakdown lines are in the trade currency but the
              row's Net Due / Pool after are in the settlement currency at the COB (T-1) rate — spell
              it out so the drill-down reconciles (lines don't sum to the totals across currencies). */}
          {settleCcy.toUpperCase() !== call.currency.toUpperCase() && (
            <p className="py-1.5 pl-6 pr-4 text-[11px] text-gray-400">
              Lines in {call.currency}; netted totals settled in {settleCcy} at COB FX{' '}
              {fxPairLabel(call.currency, settleCcy, call.cob_fx_rate)}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export interface RepoCashPoolLedgerProps {
  /** Posted calls newest-first (the API order). */
  calls: RepoMarginCall[];
  /** LR-498: start with every row expanded — used by the History "By date" single-posting view. */
  defaultExpanded?: boolean;
  /** LR-577 (feedback #69): where the breakdown's Trade ID links should return to. Required, not
   *  optional — this component has two hosts (History and the MTM drawer) and a silently droppable
   *  origin still compiles while breaking the back control (the LR-478 `cp` trap). */
  origin: TradeDetailSearch;
}

/** The cash-pool posting ledger: Date · Net Due · Direction · Pool after · By, each row expandable
 *  into its per-trade breakdown. Reused by the drawer, the History subsection, and the LR-498
 *  master–detail shell. */
export const RepoCashPoolLedger: React.FC<RepoCashPoolLedgerProps> = ({
  calls,
  defaultExpanded = false,
  origin,
}) => {
  const [expanded, setExpanded] = useState<Set<number>>(
    () => new Set(defaultExpanded ? calls.map((c) => c.id) : [])
  );
  const toggle = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  if (calls.length === 0) {
    return <p className="px-4 py-3 text-sm text-gray-500">No cash-pool postings yet.</p>;
  }

  return (
    <div>
      <div className={clsx(ROW_GRID, 'h-[39px] border-b border-gray-200 bg-lr-surface-2 pl-3 pr-5')}>
        <span aria-hidden />
        <span className="text-xs font-medium uppercase tracking-wide text-gray-500">Date</span>
        <span className="text-right text-xs font-medium uppercase tracking-wide text-gray-500">Net Due</span>
        <span className="text-xs font-medium uppercase tracking-wide text-gray-500">Direction</span>
        <span className="text-right text-xs font-medium uppercase tracking-wide text-gray-500">Pool after</span>
        <span className="text-xs font-medium uppercase tracking-wide text-gray-500">By</span>
      </div>
      {calls.map((call) => (
        <LedgerRow
          key={call.id}
          call={call}
          expanded={expanded.has(call.id)}
          onToggle={() => toggle(call.id)}
          origin={origin}
        />
      ))}
    </div>
  );
};
